import {
  ArrowRight,
  CalendarClock,
  CalendarPlus,
  ChevronRight,
  ExternalLink,
  Mail,
  MapPin,
  PencilLine,
  Phone,
  Trash2,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/Badge';
import { Button, buttonClassName } from '@/components/ui/Button';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { SecureImage } from '@/components/ui/SecureImage';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  PAYMENT_STATUS_LABEL,
  PAYMENT_STATUS_TONE,
  SCHOOL_STATUS_LABEL,
  SCHOOL_STATUS_TONE,
} from '@/constants/institute';
import { formatCurrency, formatDate } from '@/lib/format';
import { cn, getNameInitials, telHref } from '@/lib/utils';
import type { InstituteListItem } from '@/types/institute';
import { useIsSuperAdmin } from '../../hooks/useIsSuperAdmin';
import { institutesApi } from '../api/institutes.api';
import { instituteMapsUrl, isSubscriptionOverdue } from '../utils';
import { useInstituteLogo } from '../hooks/useInstituteLogo';
import { EmailInstituteDialog } from './EmailInstituteDialog';
import { HeadcountStrip } from './HeadcountStrip';
import { InstituteLogoMark } from './InstituteLogoMark';

export interface InstituteCardProps {
  institute: InstituteListItem;
  /**
   * Opens the edit form. Held by the page, which owns the routing — and passed
   * only to a viewer who may edit, so support is not offered a form it cannot
   * save.
   */
  onEdit?: (institute: InstituteListItem) => void;
  /**
   * Offers Remove. Passed by the page only for a suspended institute, and only
   * to a viewer who may remove it.
   */
  onRemove?: (institute: InstituteListItem) => void;
  className?: string;
}

/**
 * One customer, as a card.
 *
 * The console showed these as table rows, and a row is the wrong shape for what
 * this list holds: a name, a place, three adoption numbers, a feature tracker
 * and a status do not sit on one line without becoming a wall of small text.
 * As a card each of those gets its own band — identity, numbers, features,
 * contact — and the eye can compare the same band across cards.
 *
 * **The card itself is not a link.** Only four things inside it are, and each
 * goes where its own words say: the name and "View more reports" open the
 * record, the features band opens that institute's tracker, and Edit opens the
 * form. A whole-card link would swallow all of them — every click landing on the record, whatever the
 * reader thought they were pressing.
 */
export function InstituteCard({ institute, onEdit, onRemove, className }: InstituteCardProps) {
  const { tracked, enabled, names } = institute.features;
  const dueDate = institute.subscriptionDueDate;
  const isOverdue = isSubscriptionOverdue(dueDate, institute.paymentStatus);
  const phone = institute.adminContactPhone;
  const [isEmailOpen, setIsEmailOpen] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const logo = useInstituteLogo(institute.uuid, institute.hasLogo, setLogoError);
  const canEditLogo = useIsSuperAdmin();

  return (
    <article
      className={cn(
        'flex flex-col gap-4 rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5 shadow-[0_4px_16px_rgba(0,0,0,0.04)] transition-all hover:shadow-[0_8px_24px_rgba(0,0,0,0.07)] hover:border-indigo-200 dark:hover:border-indigo-900',
        className,
      )}
    >
      {/* ---------------- who ---------------- */}
      <div className="flex items-start gap-3">
        {/* The institute's own crest where one has been uploaded, its initials
            where one has not — so the row never has a hole in it while the
            image is loading or the upload never happened. The logo is private,
            so it is fetched with the token like any other read (§11). */}
        <span
          className={cn(
            'grid h-12 w-12 shrink-0 place-items-center overflow-hidden rounded-xl border border-slate-200 dark:border-slate-700 shadow-2xs',
            logo.hasLogo ? 'bg-white dark:bg-slate-800' : 'bg-indigo-50 text-[#5b52f9] dark:bg-indigo-950/60 dark:text-indigo-400',
          )}
        >
          {/* Replaced or removed from the list itself, so tidying a customer's
              branding does not mean opening every record in turn. */}
          <InstituteLogoMark
            canEdit={canEditLogo}
            hasLogo={logo.hasLogo}
            displayName={institute.displayName}
            isUploading={logo.isUploading}
            onSelect={logo.upload}
            onRemove={logo.remove}
            onInvalid={setLogoError}
          >
            {logo.hasLogo ? (
              <SecureImage
                path={institutesApi.logoPath(institute.uuid)}
                alt={`${institute.displayName} logo`}
                reloadKey={logo.version}
                className="h-full w-full p-1"
                fallback={<InstituteInitials name={institute.displayName} />}
              />
            ) : (
              <InstituteInitials name={institute.displayName} />
            )}
          </InstituteLogoMark>
        </span>

        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[16px] font-black leading-tight text-slate-900 dark:text-white">
            <Link
              to={`/platform/institutes/${institute.uuid}`}
              className="rounded-field transition-colors hover:text-[#5b52f9] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              {institute.displayName}
            </Link>
          </h3>
          <p className="mt-1 flex min-w-0 items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium">
            <span className="shrink-0 font-mono font-bold uppercase text-slate-700 dark:text-slate-300">{institute.code}</span>
            <span aria-hidden>·</span>
            {/* The place opens in Maps — the quickest answer to "where is this
                school?" when planning a visit or checking an onboarding. A new
                tab, so the console keeps its filters and scroll position. */}
            <a
              href={instituteMapsUrl(institute)}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${institute.city}, ${institute.state} — open ${institute.displayName} in Google Maps (new tab)`}
              className="flex min-w-0 items-center gap-1 rounded-field transition-colors hover:text-[#5b52f9] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            >
              <MapPin className="h-3 w-3 shrink-0 text-[#5b52f9]" aria-hidden />
              <span className="truncate underline decoration-slate-300 dark:decoration-slate-700 underline-offset-2 hover:decoration-[#5b52f9]">
                {institute.city}, {institute.state}
              </span>
              <ExternalLink className="h-3 w-3 shrink-0" aria-hidden />
            </a>
          </p>
        </div>

        <Badge tone={SCHOOL_STATUS_TONE[institute.status]} withDot className="shrink-0">
          {SCHOOL_STATUS_LABEL[institute.status]}
        </Badge>
      </div>

      {logoError && (
        <p role="alert" className="-mt-2 text-xs text-crit">
          {logoError}
        </p>
      )}

      {/* ---------------- who they have ---------------- */}
      <HeadcountStrip
        headcount={institute.headcount}
        signedIn={institute.signedIn}
        action={
          <Link
            to={`/platform/institutes/${institute.uuid}/report`}
            aria-label={`View more reports for ${institute.displayName}`}
            className="inline-flex shrink-0 items-center gap-1 rounded-field text-xs font-bold text-[#5b52f9] transition-colors hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
          >
            View more reports
            <ArrowRight className="h-3 w-3" aria-hidden />
          </Link>
        }
      />

      {/* ---------------- what they pay us ----------------
          The licence fee, whether it has arrived, and by when it should. A dash
          rather than ₹0 when nothing has been agreed: no fee settled yet and a
          fee of nothing are different answers, and only one of them is a free
          pilot. An unpaid fee past its date turns red — that is the customer
          to ring first.

          The whole band opens the payment history, as the features band opens
          the tracker: every figure on it is a question that screen answers. */}
      <Link
        to={`/platform/institutes/${institute.uuid}/subscription`}
        aria-label={`Subscription payments for ${institute.displayName}`}
        className={cn(
          'flex flex-col gap-1 rounded-xl border border-slate-200/90 dark:border-slate-800 bg-gradient-to-b from-slate-50/70 to-white dark:from-slate-800/40 dark:to-slate-900 px-3 py-2.5 text-left',
          'transition-all hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        )}
      >
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Subscription
            <ChevronRight className="h-3 w-3 text-[#5b52f9]" aria-hidden />
          </span>
          <span className="flex items-center gap-2">
            <span
              className={cn(
                'text-sm font-black tabular-nums',
                institute.subscriptionAmount === null ? 'text-slate-400 dark:text-slate-500 font-semibold' : 'text-slate-900 dark:text-white',
              )}
            >
              {formatCurrency(institute.subscriptionAmount)}
            </span>
            <Badge tone={PAYMENT_STATUS_TONE[institute.paymentStatus]} withDot>
              {PAYMENT_STATUS_LABEL[institute.paymentStatus]}
            </Badge>
          </span>
        </div>
        <p
          className={cn(
            'flex items-center gap-1.5 text-xs font-medium',
            isOverdue ? 'font-bold text-rose-600 dark:text-rose-400' : dueDate ? 'text-slate-600 dark:text-slate-300' : 'text-slate-400 dark:text-slate-500',
          )}
        >
          <CalendarClock className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {dueDate === null
            ? 'No due date set'
            : `${isOverdue ? 'Overdue since' : 'Due'} ${formatDate(dueDate)}`}
        </p>
        {/* Said in words as well as by the chevron, so the band reads as a way
            in rather than as a panel of facts. */}
        <span className="mt-0.5 inline-flex items-center gap-1 text-xs font-bold text-[#5b52f9]">
          Payment history
          <ArrowRight className="h-3 w-3" aria-hidden />
        </span>
      </Link>

      {/* ---------------- what they asked us for ----------------
          Asked for, built, switched on — three numbers because the gaps between
          them are the point. The bar measures the last against the first: how
          much of what this customer asked for they can actually use.

          The whole band is the link to this institute's tracker, because that
          is the screen every one of these numbers is asking you to open. */}
      <Link
        to={`/platform/features?institute=${institute.uuid}`}
        aria-label={`Features for ${institute.displayName}`}
        className={cn(
          'flex flex-col gap-2 rounded-xl border border-slate-200/90 dark:border-slate-800 bg-gradient-to-b from-slate-50/70 to-white dark:from-slate-800/40 dark:to-slate-900 p-3 text-left',
          'transition-all hover:border-indigo-300 dark:hover:border-indigo-700 hover:shadow-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        )}
      >
        <div className="flex items-baseline justify-between gap-2">
          <span className="flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            Features
            <ChevronRight className="h-3 w-3 text-[#5b52f9]" aria-hidden />
          </span>
          {tracked > 0 && (
            <span className="whitespace-nowrap text-xs text-ink-2">
              <span className="tabular-nums">{tracked}</span> asked ·{' '}
              <span className={cn('tabular-nums font-[560]', enabled === 0 ? 'text-warn' : 'text-ink')}>
                {enabled}
              </span>{' '}
              live
            </span>
          )}
        </div>

        {tracked === 0 ? (
          <p className="text-xs text-slate-400 dark:text-slate-500 font-medium">Nothing tracked yet — open the tracker to add some.</p>
        ) : (
          <>
            <ProgressBar
              value={(enabled / tracked) * 100}
              label={`Features live at ${institute.displayName}`}
              hideLabel
              size="sm"
            />
            {/* Two names fit; the rest go behind a count, with the full list on
                the tooltip — "which features?" is the next question after "how
                many", and answering it here saves opening the tracker. */}
            <div className="flex flex-wrap gap-1" title={names.join(', ')}>
              {names.slice(0, 2).map((name) => (
                <span key={name} className="pill bg-slate-100 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-[11px] font-semibold">
                  {name}
                </span>
              ))}
              {names.length > 2 && (
                <span className="pill bg-slate-100 dark:bg-slate-800 border border-slate-200/60 dark:border-slate-700 text-slate-500 dark:text-slate-400 text-[11px] font-semibold">+{names.length - 2} more</span>
              )}
            </div>
          </>
        )}
      </Link>

      {/* ---------------- who we talk to ----------------
          Each way of reaching the administrator sits on the same line as the
          button that uses it, so the address and the action read as one thing
          instead of a list of details above a row of unlabelled icons.

          Call is a `tel:` link, because only an anchor can hand off to the
          phone's dialler. Email opens our own composer, so the message — and
          its attachments — leave from the console and are on the audit trail.
          Both reach the administrator, the person we deal with, not the
          school's front office. */}
      <div className="mt-auto flex flex-col divide-y divide-slate-100 dark:divide-slate-800 rounded-xl border border-slate-200/90 dark:border-slate-800 overflow-hidden bg-slate-50/40 dark:bg-slate-800/30">
        <ContactRow icon={<Mail className="h-3.5 w-3.5" aria-hidden />} value={institute.adminContactEmail}>
          <Button
            variant="secondary"
            size="sm"
            iconOnly
            aria-label={`Email ${institute.displayName}'s administrator at ${institute.adminContactEmail}`}
            title={`Email ${institute.adminContactEmail}`}
            onClick={() => setIsEmailOpen(true)}
          >
            <Mail className="h-3.5 w-3.5" aria-hidden />
          </Button>
        </ContactRow>
        {phone && (
          <ContactRow
            icon={<Phone className="h-3.5 w-3.5" aria-hidden />}
            value={<span className="tabular-nums font-mono text-sm">{phone}</span>}
          >
            <a
              href={telHref(phone)}
              aria-label={`Call ${institute.displayName}'s administrator on ${phone}`}
              title={`Call ${phone}`}
              className={buttonClassName({ variant: 'secondary', size: 'sm', iconOnly: true })}
            >
              <Phone className="h-3.5 w-3.5" aria-hidden />
            </a>
          </ContactRow>
        )}
      </div>

      {/* The record's housekeeping, set apart from the contact details by a
          ruled band along the card's foot. */}
      <div className="-mx-4 sm:-mx-5 -mb-4 sm:-mb-5 flex items-center justify-between gap-3 rounded-b-[inherit] border-t border-slate-100 dark:border-slate-800 bg-slate-50/90 dark:bg-slate-800/60 px-4 sm:px-5 py-2.5">
        <span className="flex min-w-0 items-center gap-1.5 text-xs text-slate-500 dark:text-slate-400 font-medium">
          <CalendarPlus className="h-3.5 w-3.5 shrink-0 text-[#5b52f9]" aria-hidden />
          <span className="truncate">Added {formatDate(institute.createdAt)}</span>
        </span>
        <div className="flex shrink-0 items-center gap-2">
          {onRemove && (
            <Button
              variant="dangerSoft"
              size="sm"
              aria-label={`Remove ${institute.displayName}`}
              leftIcon={<Trash2 className="h-3.5 w-3.5" />}
              onClick={() => onRemove(institute)}
            >
              Remove
            </Button>
          )}
          {onEdit && (
            <Button
              variant="secondary"
              size="sm"
              aria-label={`Edit ${institute.displayName}`}
              leftIcon={<PencilLine className="h-3.5 w-3.5" />}
              onClick={() => onEdit(institute)}
            >
              Edit
            </Button>
          )}
        </div>
      </div>

      <EmailInstituteDialog
        isOpen={isEmailOpen}
        onClose={() => setIsEmailOpen(false)}
        institute={institute}
      />
    </article>
  );
}

/** One way of reaching the administrator, with the button that uses it. */
function ContactRow({
  icon,
  value,
  children,
}: {
  icon: ReactNode;
  value: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2">
      <span
        className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-indigo-50 text-[#5b52f9] dark:bg-indigo-950/60 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/50"
        aria-hidden
      >
        {icon}
      </span>
      <span className="min-w-0 flex-1 truncate text-sm font-semibold text-slate-700 dark:text-slate-300">{value}</span>
      {children}
    </div>
  );
}

/** The mark drawn when there is no crest, or one that will not load. */
function InstituteInitials({ name }: { name: string }) {
  return (
    <span aria-hidden className="text-sm font-[650] text-accent">
      {getNameInitials(name)}
    </span>
  );
}

/**
 * The card's own loading shape.
 *
 * Lives beside it so the two cannot drift: a placeholder that stops matching
 * the card it stands in for makes the grid jump the moment data arrives.
 */
export function InstituteCardSkeleton() {
  return (
    <div
      className="card flex flex-col gap-4 p-4"
      role="status"
      aria-label="Loading institute"
    >
      <div className="flex items-start gap-3">
        <Skeleton className="h-11 w-11" />
        <div className="flex flex-1 flex-col gap-2">
          <Skeleton className="h-3.5 w-2/3" />
          <Skeleton className="h-3 w-1/2" />
        </div>
      </div>

      <div className="flex flex-col gap-2">
        <Skeleton className="h-16 w-full" />
        <Skeleton className="h-3 w-full" />
      </div>

      <div className="flex flex-col gap-2">
        <Skeleton className="h-3 w-1/3" />
        <Skeleton className="h-1.5 w-full rounded-pill" />
        <Skeleton className="h-4 w-2/3 rounded-pill" />
      </div>

      <div className="flex flex-col gap-3 border-t border-line pt-3">
        <div className="flex flex-col gap-2">
          <Skeleton className="h-3 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-3 w-1/3" />
        </div>
        <Skeleton className="h-8 w-full" />
      </div>
    </div>
  );
}
